import http from "./http.service";
import wsService from "./ws.service";
import chatService from "./chat.service";
import pushService from "./push.service";

/**
 * La campana del sitio y, de paso, el arranque de todo lo que llega en vivo.
 *
 * <p>Concentra tres cosas que en la práctica son una sola —"avisarle al usuario que pasó algo"—: la
 * lista de notificaciones sin ver, la conexión WebSocket por la que entran las nuevas, y el registro
 * del dispositivo para push. Están juntas porque comparten el ciclo de vida: nacen cuando el usuario
 * se loguea y mueren cuando cierra sesión, y separarlas obligaba a acordarse de inicializar y
 * limpiar tres cosas en cada uno de esos dos puntos.
 */

export interface Notificacion {
    id: number;
    type: string;
    title: string;
    body?: string;
    deepLink?: string;
    forSeller?: boolean;
    viewed?: boolean;
    createdInstant?: string;
}

class NotificacionService {

    notificaciones: Notificacion[] = [];

    private suscriptores: ((n: Notificacion[]) => void)[] = [];

    private desuscribirWs: (() => void)[] = [];

    /** Avisa cada vez que cambia la lista. Devuelve cómo dejar de escuchar. */
    onCambio = (cb: (n: Notificacion[]) => void): (() => void) => {
        this.suscriptores.push(cb);
        cb(this.notificaciones);
        return () => {
            this.suscriptores = this.suscriptores.filter((s) => s !== cb);
        };
    };

    private emitir = () => this.suscriptores.forEach((s) => s(this.notificaciones));

    get sinLeer(): number {
        return this.notificaciones.filter((n) => !n.viewed).length;
    }

    /** Arranca todo. Lo llama authService cuando ya hay sesión. */
    public initialize = () => {
        this.cargar();
        chatService.refrescarNoLeidos();
        this.conectarWs();
        pushService.initialize();
    };

    /** Corta todo. Sin esto, el socket del usuario anterior sigue vivo tras un cambio de cuenta. */
    public shutdown = () => {
        this.desuscribirWs.forEach((f) => f());
        this.desuscribirWs = [];
        wsService.disconnect();
        this.notificaciones = [];
        chatService.reset();
        this.emitir();
    };

    public cargar = () =>
        http.get("/api/notificaciones/current").then(
            (data: any) => {
                // El backend las devuelve bajo `notifications`; la clave puede no venir si no hay
                // ninguna, así que el fallback no es opcional.
                this.notificaciones = data?.notifications || [];
                this.emitir();
                return this.notificaciones;
            },
            (error: any) => {
                console.error(error);
                return this.notificaciones;
            }
        );

    public markAsViewed = (id: number) =>
        http.post("/api/notificaciones/mark-as-viewed", { id }).then(
            () => {
                this.notificaciones = this.notificaciones.filter((it) => it.id !== id);
                this.emitir();
            },
            (error: any) => console.error(error)
        );

    public markAllAsViewed = () =>
        http.post("/api/notificaciones/mark-all-as-viewed", {}).then(
            () => {
                this.notificaciones = [];
                this.emitir();
            },
            (error: any) => console.error(error)
        );

    /**
     * Conecta el socket y engancha las dos colas personales.
     *
     * <p>Son dos y no una porque son dos cosas distintas: `/queue/chat` trae el mensaje entero —lo
     * necesita la bandeja para mover el hilo arriba sin recargar— y `/queue/notificaciones` trae el
     * aviso ya formateado para la campana. Un solo canal obligaría a que cada oyente descarte lo que
     * no le sirve.
     */
    private conectarWs = () => {
        const raw = localStorage.getItem("userAutopartes");
        const token = raw ? JSON.parse(raw)?.token : undefined;
        if (!token) return;

        wsService.connect(token);

        this.desuscribirWs.push(
            wsService.subscribe("/user/queue/notificaciones", (noti: any) => {
                this.notificaciones = [
                    {
                        id: noti.id,
                        type: noti.type,
                        title: noti.title,
                        body: noti.body,
                        deepLink: noti.deepLink,
                        forSeller: noti.forSeller,
                        viewed: false,
                        createdInstant: noti.createdInstant,
                    },
                    ...this.notificaciones,
                ];
                this.emitir();
            })
        );

        this.desuscribirWs.push(
            wsService.subscribe("/user/queue/chat", (msg: any) => {
                chatService.sumarNoLeido(msg?.tipo === "PEDIDO" ? "PEDIDO" : "CONSULTA");
                // La bandeja abierta escucha esto para mover el hilo al tope sin volver a pedirla.
                window.dispatchEvent(new CustomEvent("chat-mensaje", { detail: msg }));
            })
        );
    };
}

const notificacionService = new NotificacionService();

export default notificacionService;
